import { useCallback, useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';
import { SafeURL } from '../../../utils/safeUrl';

/**
 * Custom hook to manage attribId per assetId using state only
 * Supports:
 *  - Web URL
 *  - iOS Universal Links
 *  - Android App Links
 */
export function useAttributionIdStorage() {
    const [assetId, setAssetId] = useState<string | null>(null);
    const [attributionId, setAttribId] = useState<string | null>(null);

    // -----------------------------
    // Save attribId for assetId
    // -----------------------------
    const saveAttributionId = useCallback((asset: string, attrib: string) => {
        if (!asset || !attrib) return;
        setAssetId(asset);
        setAttribId(attrib);
    }, []);

    // -----------------------------
    // Remove attribId for assetId
    // -----------------------------
    const removeAttribId = useCallback(
        (asset: string) => {
            if (assetId !== asset) {
                setAssetId(null);
                setAttribId(null);
            }
        },
        [assetId]
    );

    // -----------------------------
    // Parse attribId + assetId from a URL
    // -----------------------------
    const parseFromURL = useCallback(
        (urlString: string | null) => {
            if (!urlString) return;

            try {
                const parsedUrl = new SafeURL(urlString);

                // Support: attribution_id, attributionId
                const attrib =
                    parsedUrl.searchParams.get('attribution_id') ||
                    parsedUrl.searchParams.get('attributionId') ||
                    '';

                // URL format: /player/movie/<assetId>
                const parts = parsedUrl.pathname.split('/');
                const maybeAssetId = parts[3];

                if (maybeAssetId && attrib) {
                    saveAttributionId(maybeAssetId, attrib);
                }
            } catch (e) {
                console.warn('Invalid URL', urlString, e);
            }
        },
        [saveAttributionId]
    );

    // -----------------------------
    // UseEffect → Web + Mobile Deeplinks
    // -----------------------------
    useEffect(() => {
        // Web support (browser refresh)
        if (Platform.OS === 'web') {
            parseFromURL(window.location.href);
            return;
        }

        // Mobile: Cold start link
        Linking.getInitialURL().then((url) => {
            if (url) parseFromURL(url);
        });

        // Mobile: When app is already open
        Linking.addEventListener('url', (event) => {
            parseFromURL(event.url);
        });
    }, [parseFromURL]);

    return {
        assetId,
        attributionId,
        saveAttributionId,
        removeAttribId,
    };
}
